/**
 * Centralized mapping of localized slugs for custom pages
 */

export const localizedSlugs = {
  '7laux': {
    en: '7laux-ski-mtb-snowboard-snowscoot-rental-repair-preparation',
    fr: '7laux-location-ski-vtt-snowboard-snowscoot-reparation-preparation',
    nl: '7laux-ski-mtb-snowboard-snowscoot-verhuur-reparatie-voorbereiding',
    es: '7laux-alquiler-ski-mtb-snowboard-snowscoot-reparacion-preparacion',
    it: '7laux-noleggio-sci-mtb-snowboard-snowscoot-riparazione-preparazione'
  },
  'contact-summer': {
    en: '7laux-rental-booking-maintenance-workshop-mtb-downhill-kids-protection',
    fr: '7laux-location-reservation-entretien-atelier-vtt-descente-enfant-protection',
    nl: '7laux-verhuur-boeking-onderhoud-werkplaats-mtb-downhill-kinderen-bescherming',
    es: '7laux-alquiler-reserva-mantenimiento-taller-mtb-descenso-ninos-proteccion',
    it: '7laux-noleggio-prenotazione-manutenzione-officina-mtb-discesa-bambini-protezione'
  },
  'contact-winter': {
    en: '7laux-rental-booking-maintenance-workshop-ski-snowboard-snowscoot',
    fr: '7laux-location-reservation-entretien-atelier-ski-snowboard-snowscoot',
    nl: '7laux-verhuur-boeking-onderhoud-werkplaats-ski-snowboard-snowscoot',
    es: '7laux-alquiler-reserva-mantenimiento-taller-ski-snowboard-snowscoot',
    it: '7laux-noleggio-prenotazione-manutenzione-officina-sci-snowboard-snowscoot'
  },
  '7laux-winter': {
    en: '7laux-ski-snowboard-ski-area-alps-families',
    fr: '7laux-ski-snowboard-domaine-skiable-alpes-familles',
    nl: '7laux-ski-snowboard-skigebied-alpen-gezinnen',
    es: '7laux-esqui-snowboard-estacion-esqui-alpes-familias',
    it: '7laux-sci-snowboard-comprensorio-sciistico-alpi-famiglie'
  },
  '7laux-summer': {
    en: '7laux-bike-park-mtb-enduro-downhill-ebike-alps',
    fr: '7laux-bike-parc-vtt-enduro-descente-ebike-alpes',
    nl: '7laux-bikepark-mtb-enduro-downhill-ebike-alpen',
    es: '7laux-parque-bicicletas-mtb-enduro-descenso-ebike-alpes',
    it: '7laux-bike-park-mtb-enduro-discesa-ebike-alpi'
  },
  'location-summer': {
    en: '7laux-mtb-rental-downhill-ebike',
    fr: '7laux-location-vtt-descente-ebike',
    nl: '7laux-mtb-verhuur-downhill-ebike',
    es: '7laux-alquiler-mtb-descenso-ebike',
    it: '7laux-noleggio-mtb-discesa-ebike'
  },
  'location-winter': {
    en: '7laux-ski-snowboard-rental-equipment',
    fr: '7laux-location-ski-snowboard-equipement',
    nl: '7laux-ski-snowboard-verhuur-uitrusting',
    es: '7laux-alquiler-ski-snowboard-equipamiento',
    it: '7laux-noleggio-sci-snowboard-attrezzatura'
  },
  'atelier-summer': {
    en: '7laux-mtb-workshop-repair-maintenance',
    fr: '7laux-atelier-vtt-reparation-entretien',
    nl: '7laux-mtb-werkplaats-reparatie-onderhoud',
    es: '7laux-taller-mtb-reparacion-mantenimiento',
    it: '7laux-officina-mtb-riparazione-manutenzione'
  },
  'atelier-winter': {
    en: '7laux-ski-workshop-wax-tuning',
    fr: '7laux-atelier-ski-fartage-affutage',
    nl: '7laux-ski-werkplaats-waxen-slijpen',
    es: '7laux-taller-ski-encerado-afilado',
    it: '7laux-officina-sci-sciolinatura-affilatura'
  },
  'location-atelier-summer': {
    en: '7laux-mtb-downhill-rental-workshop-repair-preparation',
    fr: '7laux-location-vtt-descente-atelier-reparation-preparation',
    nl: '7laux-mtb-downhill-verhuur-werkplaats-reparatie-voorbereiding',
    es: '7laux-alquiler-mtb-descenso-taller-reparacion-preparacion',
    it: '7laux-noleggio-mtb-discesa-officina-riparazione-preparazione'
  },
  'location-atelier-winter': {
    en: '7laux-ski-snowboard-snowscoot-rental-workshop-repair-preparation',
    fr: '7laux-location-ski-snowboard-snowscoot-atelier-reparation-preparation',
    nl: '7laux-ski-snowboard-snowscoot-verhuur-werkplaats-reparatie-voorbereiding',
    es: '7laux-alquiler-ski-snowboard-snowscoot-taller-reparacion-preparacion',
    it: '7laux-noleggio-sci-snowboard-snowscoot-officina-riparazione-preparazione'
  },
} as const

export type LocaleCode = 'en' | 'fr' | 'nl' | 'es' | 'it'

/**
 * Get the localized slug for a given page and locale
 */
export function getLocalizedSlug(pageKey: keyof typeof localizedSlugs, locale: LocaleCode): string {
  const slugs = localizedSlugs[pageKey] as Record<LocaleCode, string>
  return slugs[locale] || slugs['en'] || ''
}

/**
 * Find the page key from a slug
 */
export function getPageKeyFromSlug(slug: string): keyof typeof localizedSlugs | null {
  for (const [key, slugs] of Object.entries(localizedSlugs)) {
    const slugValues = Object.values(slugs) as string[]
    if (slugValues.includes(slug)) {
      return key as keyof typeof localizedSlugs
    }
  }
  return null
}

/**
 * Get the current locale from a slug
 */
export function getLocaleFromSlug(slug: string): LocaleCode | null {
  for (const [, slugs] of Object.entries(localizedSlugs)) {
    for (const [locale, localeSlug] of Object.entries(slugs)) {
      if (localeSlug === slug) {
        return locale as LocaleCode
      }
    }
  }
  return null
}
